/**
 * Recognises the failure a code-split app produces when a lazy chunk cannot be
 * fetched — a network blip, an adblocker filter rule matching a chunk filename,
 * a CDN edge failure, or a tab outliving the assets it was built against.
 *
 * These are environmental and recoverable (a reload gets the current entry
 * bundle), so they belong at `warn` rather than `error`, and they should not
 * put a "tell us what happened" dialog in front of the user.
 *
 * Matching is on message substrings because there is no error subclass to test:
 * each engine words it differently, and Vite adds its own for a stylesheet it
 * could not preload. Substring rather than exact match, since every engine
 * appends the failing URL.
 *
 * Two consumers must stay in agreement, which is why this lives in
 * `@eluve/utils` rather than next to either of them: `ErrorFallbackRender`
 * (which picks the recovery UI per error) and `initializeSentry` (which turns
 * these fragments into `ignoreErrors` so Sentry's own boundary capture — which
 * fires unconditionally, before any fallback renders — cannot report a
 * recoverable chunk failure as an error event).
 */
export const CHUNK_LOAD_ERROR_FRAGMENTS = [
  // Chromium
  'failed to fetch dynamically imported module',
  // Firefox
  'error loading dynamically imported module',
  // Safari
  'importing a module script failed',
  // Vite's preload helper, for a stylesheet dependency
  'unable to preload css for',
];

export const isChunkLoadError = (error: unknown): boolean => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';

  if (!message) return false;

  const normalized = message.toLowerCase();
  return CHUNK_LOAD_ERROR_FRAGMENTS.some((fragment) =>
    normalized.includes(fragment),
  );
};
